'use client'

import { REAL_ESTATE_PARTNERS_MAP } from '@/app/[lang]/partners/real-estate-partners'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import Image from 'next/image'
import Link from 'next/link'
import { FaBookOpen, FaHandsHelping, FaLinkedin, FaNewspaper, FaQuoteLeft, FaQuoteRight, FaTwitter, FaVideo } from 'react-icons/fa'
import { getI18n, getLanguage, I18nDictionary, LangCode, useI18nValue } from '../i18n/lang-context'
import { Flex } from '../ui/flex'
import { Article, ArticleType } from './press-articles'
import { cn } from '@/lib/utils/utils'

interface PressArticleCardProps {
  article: Article
  langCode: LangCode
  className?: string
  isLink?: boolean
}

const I18N: I18nDictionary = {
  press: {
    fr: 'Article Presse',
    en: 'Press Article'
  },
  web: {
    fr: 'Article Web',
    en: 'Web Article'
  },
  twitter: {
    fr: 'Post X/Twitter',
    en: 'X/Twitter Post'
  },
  linkedin: {
    fr: 'Post LinkedIn',
    en: 'LinkedIn Post'
  },
  partnership: {
    fr: 'Annonce de Partenariat',
    en: 'Partnership Announcement'
  },
  video: {
    fr: 'Vidéo',
    en: 'Video'
  },
}
const TypeIcon = ({ type }: { type: ArticleType }) => {
  if(type === 'press')
    return <FaBookOpen className='mr-1.5 h-3 w-3' />
  if(type === 'web')
    return <FaNewspaper className='mr-1.5 h-3 w-3' />
  if(type === 'twitter')
    return <FaTwitter className='mr-1.5 h-3 w-3' />
  if(type === 'linkedin')
    return <FaLinkedin className='mr-1.5 h-3 w-3' />
  if(type === 'partnership')
    return <FaHandsHelping className='mr-1.5 h-3 w-3' />
  if(type === 'video')
    return <FaVideo className='mr-1.5 h-3 w-3' />
  return null
}

export function PressArticleCard({
  article,
  langCode,
  className = '',
  isLink = true
}: PressArticleCardProps) {
  const i18n = getI18n(I18N, langCode)
  const i18nValue = useI18nValue()
  const title = i18nValue(article.titre, langCode)
  const url = i18nValue(article.url, langCode)
  const citation = i18nValue(article.citation, langCode)
  const type = i18n[article.type]
  const media = typeof article.source === 'string' ? REAL_ESTATE_PARTNERS_MAP[article.source].name : article.source.media

  const hasLogoDark =  typeof article.source !== 'string' || !!REAL_ESTATE_PARTNERS_MAP[article.source].logoDark
  const logo = typeof article.source !== 'string' ? article.source.logo_url : REAL_ESTATE_PARTNERS_MAP[article.source].logo
  const logoDark = typeof article.source !== 'string' ? article.source.logo_url : !hasLogoDark ? REAL_ESTATE_PARTNERS_MAP[article.source].logo : REAL_ESTATE_PARTNERS_MAP[article.source].logoDark as string

  const locale = getLanguage(langCode, false)
  const cardContent = (
    <Card className='flex h-full flex-col'>
      <CardHeader>
        <div className='flex items-center gap-2 pt-1 text-sm'>
          <Flex className='flex-nowrap items-center gap-2'>
            {
              hasLogoDark ? <>
                <Image
                  src={logo}
                  alt={`Logo ${media}`}
                  width={25}
                  height={25}
                  className={cn('h-8 w-8 object-contain bg-card dark:hidden block')}
                />
                <Image
                  src={logoDark}
                  alt={`Logo ${media}`}
                  width={25}
                  height={25}
                  className={cn('h-8 w-8 object-contain bg-card hidden dark:block')}
                /></>
              :
                <Image
                  src={logo}
                  alt={`Logo ${media}`}
                  width={25}
                  height={25}
                  className={cn('h-8 w-8 object-contain bg-card dark:bg-white')}
                />
            }
            <span>
              {media}
            </span>
          </Flex>
          <span className='grow' />
          <span className='whitespace-nowrap text-xs'>
            {new Date(article.date).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric'
            })}
          </span>
        </div>
        <CardTitle className='mt-1 text-lg font-semibold leading-tight'>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className='flex grow flex-col justify-center'>
        <blockquote className='relative border-l-4 border-primary py-2 pl-8 pr-4 italic'>
          <FaQuoteLeft className='absolute left-2 top-0 text-xl text-primary opacity-50' />
          {article.type === 'video' ?
            <Image id={title} src={citation} alt={title}
              className={'overflow-hidden rounded-md place-self-center'}
              width={125}
              height={125}
              sizes='100vw'/> :
            <p className='line-clamp-6 text-sm'>{citation}</p>}
          <FaQuoteRight className='absolute -right-0 bottom-0 text-xl text-primary opacity-50' />
        </blockquote>
      </CardContent>
      <CardFooter className='justify-end pt-4'>
        <span className='mx-2 mb-2 inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-medium text-primary'>
          <TypeIcon type={article.type} />
          {type}
        </span>
      </CardFooter>
    </Card>
  )

  if (isLink) {
    return (
      <Link
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className={`flex h-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 ease-in-out hover:-translate-y-1 hover:shadow-md ${className}`}
      >
        {cardContent}
      </Link>
    )
  }

  return (
    <a
      href={url}
      target='_blank'
      rel='noopener noreferrer'
      className={`flex h-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 ease-in-out hover:-translate-y-1 hover:shadow-md ${className}`}
    >
      {cardContent}
    </a>
  )
}
